import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import { Uri } from '#constants/uri';

/**
 * Deletes a tag from a mailing by its mailing ID and tag ID.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingId - The ID of the mailing from which the tag will be deleted.
 * @param tagId - The ID of the tag to delete from the mailing.
 * @returns A promise that resolves to an {@link OperationResult} indicating the outcome of the delete operation.
 * @throws Will throw an error if either `mailingId` or `tagId` is not provided, or if the HTTP request fails.
 */
export const deleteMailingTag = async (instance: KyInstance, mailingId?: number, tagId?: number) => {
  try {
    if (isNotDefined(mailingId) || isNotDefined(tagId)) {
      throw new Error('Mailing ID and Tag ID must be provided to delete a mailing tag.');
    }

    return await instance
      .delete<OperationResult>(formatString(Uri.mailing.deleteMailingTag, mailingId.toString(), tagId.toString()))
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
